import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, HintNote, Input } from '../admin/ui';
import * as Api from './PersonAdminApi';
import { errorText } from './personAdminModel';

// The contact email of one person with a small inline edit: the address (or "no email yet") and a
// "Change" / "Add email" link that turns it into one input. Saving is the plain person edit
// (PUT /api/cms/people/{id}, an ADMIN change; an address another live person has is refused with the
// backend's message). `editable` is false for an account holder: their email is their login and the
// admin cannot change it. `onSaved(detail)` gets the person's detail the edit answers.
const ContactEmailEdit = ({ personId, value, editable = true, onSaved }) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const valid = /^[^\s@]+@[^\s@]+$/.test(draft.trim());

  const start = () => { setDraft(value || ''); setError(null); setEditing(true); };

  const save = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const detail = await Api.savePerson(personId, { contactEmail: draft.trim() });
      setEditing(false);
      onSaved?.(detail);
    } catch (err) {
      setError(errorText(err, intl.formatMessage({ id: 'personAdmin.email.failed', defaultMessage: 'Could not save the email address.' })));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input type="email" value={draft} autoFocus style={{ flex: '1 1 12rem', minWidth: 0, padding: '.4rem .5rem' }}
            aria-label={intl.formatMessage({ id: 'personAdmin.email.label', defaultMessage: 'Contact email' })}
            placeholder="name@example.org" onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }} />
          <Button type="button" disabled={!valid || busy} onClick={save} style={{ padding: '.4rem .7rem' }}>
            <FormattedMessage id="personAdmin.email.save" defaultMessage="Save email" />
          </Button>
          <Button type="button" secondary disabled={busy} onClick={() => setEditing(false)} style={{ padding: '.4rem .7rem' }}>
            <FormattedMessage id="personAdmin.new.cancel" defaultMessage="Cancel" />
          </Button>
        </div>
        {error && <div role="alert" style={{ marginTop: '.3rem', fontSize: '.78rem', color: '#991b1b' }}>{error}</div>}
      </div>
    );
  }

  return (
    <span>
      {value
        ? <span>{value}</span>
        : <HintNote style={{ fontSize: '.85rem' }}><FormattedMessage id="personAdmin.email.none" defaultMessage="no email yet" /></HintNote>}
      {editable && (
        <button type="button" onClick={start}
          style={{ marginLeft: '.5rem', border: 0, background: 'none', padding: 0, color: '#0369a1', font: 'inherit', fontSize: '.8rem', cursor: 'pointer', textDecoration: 'underline' }}>
          {value
            ? <FormattedMessage id="personAdmin.email.change" defaultMessage="Change" />
            : <FormattedMessage id="personAdmin.email.add" defaultMessage="Add email" />}
        </button>
      )}
    </span>
  );
};

export default ContactEmailEdit;
